/**
 * Het tabblad Prompts: de prompts per categorie bekijken, bewerken, en een
 * systeemprompt terugzetten naar de standaardtekst.
 *
 * Wat hier staat, krijgt het model (sinds stap 7; daarvoor las de code deze
 * prompts niet). Een prompt heeft vaste velden, zoals {tekst}, waar de code
 * iets invult; die staan onder de prompt. Haal je er een weg of verzin je er
 * een, dan weigert de server het opslaan en staat de reden in de melding.
 *
 * Een prompt die niemand bewerkte, krijgt bij het opstarten vanzelf de nieuwe
 * standaard (stap 17c); een bewerkte blijft staan en heeft het label Bewerkt.
 * Kreeg de standaard na het bewerken een veld bij, dan staat hier welk; wat
 * daar hoort, komt pas bij het model als het veld erin staat.
 */
import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronUp, Edit2, MessageSquare, RefreshCw, RotateCcw, Save } from 'lucide-react';
import { beheerApi as api } from '../../../api/beheer';

const LEEG = { name: '', description: '', prompt_text: '' };

export function Prompts({ actief, meldFout }) {
  const [categorieen, setCategorieen] = useState([]);
  const [prompts, setPrompts] = useState([]);
  const [laden, setLaden] = useState(false);
  const [categorie, setCategorie] = useState(null);
  const [open, setOpen] = useState({});              // per prompt: uitgeklapt?
  const [bewerkt, setBewerkt] = useState(null);      // het id van de prompt in het formulier
  const [formulier, setFormulier] = useState(LEEG);
  const [bezig, setBezig] = useState(false);
  const [bericht, setBericht] = useState('');

  useEffect(() => {
    if (!actief) return;
    setLaden(true);
    Promise.all([api.haalPromptCategorieen(), api.haalPrompts()])
      .then(([cats, alle]) => { setCategorieen(cats); setPrompts(alle); })
      .catch(err => meldFout(err.message))
      .finally(() => setLaden(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- alleen bij het openen van het tabblad
  }, [actief]);

  /** De velden van een prompt als tekst: "{tekst}, {niveau}". */
const veldenAlsTekst = (velden) => velden.map(v => `{${v}}`).join(', ');

/** Het bericht drie seconden laten staan. */
  const meld = (tekst) => {
    setBericht(tekst);
    setTimeout(() => setBericht(''), 3000);
  };

  const vervang = (bijgewerkt) => setPrompts(prev => prev.map(p => (p.id === bijgewerkt.id ? bijgewerkt : p)));

  const bewaar = async (id) => {
    setBezig(true);
    setBericht('');
    try {
      vervang(await api.bewaarPrompt(id, formulier));
      setBewerkt(null);
      setFormulier(LEEG);
      meld('Prompt opgeslagen!');
    } catch (err) {
      setBericht('Fout: ' + err.message);
    } finally {
      setBezig(false);
    }
  };

  const zetTerug = async (id) => {
    if (!window.confirm('Weet je zeker dat je deze prompt wilt resetten naar de standaard waarde?')) return;
    setBezig(true);
    setBericht('');
    try {
      const bijgewerkt = await api.zetPromptTerug(id);
      vervang(bijgewerkt);
      setFormulier({ name: bijgewerkt.name, description: bijgewerkt.description || '', prompt_text: bijgewerkt.prompt_text });
      meld('Prompt gereset naar standaard waarde!');
    } catch (err) {
      setBericht('Fout: ' + err.message);
    } finally {
      setBezig(false);
    }
  };

  const begin = (prompt) => {
    setBewerkt(prompt.id);
    setFormulier({ name: prompt.name, description: prompt.description || '', prompt_text: prompt.prompt_text });
  };

  if (laden) return <p>Laden...</p>;

  const gekozen = categorieen.find(c => c.key === categorie);

  return (
    <div className="card">
      <h2 className="mb-16">Prompts Beheer</h2>
      <p className="text-muted mb-16">
        Beheer de AI prompts die worden gebruikt voor tekstverwerking, illustraties en toetsen.
      </p>

      {bericht && (
        <div className={`alert ${bericht.includes('Fout') ? 'alert-error' : 'alert-success'} mb-16`}>{bericht}</div>
      )}

      <div className="flex gap-8 mb-24" style={{ flexWrap: 'wrap' }}>
        {categorieen.map(cat => {
          const aan = categorie === cat.key;
          return (
            <button key={cat.key} className={`btn ${aan ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setCategorie(aan ? null : cat.key)} style={{ padding: '12px 20px' }}>
              {cat.name}
              <span className="badge" style={{ marginLeft: 8, background: aan ? 'var(--wit)' : 'var(--accent-oud)', color: aan ? 'var(--accent-oud)' : 'var(--wit)' }}>
                {cat.prompt_count}
              </span>
            </button>
          );
        })}
      </div>

      {!categorie ? (
        <div className="text-center text-muted" style={{ padding: 40 }}>
          <MessageSquare size={48} style={{ marginBottom: 16, color: 'var(--stiller)' }} />
          <p>Selecteer een categorie om de prompts te bekijken en bewerken.</p>
        </div>
      ) : (
        <div>
          <h3 className="mb-16">{gekozen?.name || categorie}</h3>
          <p className="text-muted mb-16">{gekozen?.description}</p>

          {prompts
            .filter(p => p.category === categorie)
            .sort((a, b) => a.sort_order - b.sort_order)
            .map(prompt => (
              <div key={prompt.id} className="card mb-8" style={{ background: 'var(--vlak)' }}>
                <div className="flex-between" style={{ cursor: 'pointer' }}
                  onClick={() => setOpen(prev => ({ ...prev, [prompt.id]: !prev[prompt.id] }))}>
                  <div style={{ flex: 1 }}>
                    <div className="flex gap-8" style={{ alignItems: 'center' }}>
                      <strong>{prompt.name}</strong>
                      {prompt.is_system && <span className="badge" style={{ background: 'var(--stiller)', fontSize: 10 }}>Systeem</span>}
                      {!prompt.is_active && <span className="badge" style={{ background: 'var(--fout-2)', fontSize: 10 }}>Inactief</span>}
                      {prompt.bewerkt && <span className="badge" style={{ background: 'var(--accent-oud)', fontSize: 10 }}>Bewerkt</span>}
                    </div>
                    {prompt.description && <p className="text-muted text-small mt-4">{prompt.description}</p>}
                    {prompt.ontbrekende_velden?.length > 0 && (
                      <p className="text-small mt-4" style={{ color: 'var(--waarschuw-tekst)' }} data-testid="ontbrekende-velden">
                        Deze bewerkte prompt mist {veldenAlsTekst(prompt.ontbrekende_velden)} uit de nieuwe standaard; wat daar
                        hoort, komt nu niet bij het model. Zet hem terug naar de standaard, of voeg het veld toe.
                      </p>
                    )}
                  </div>
                  <div className="flex gap-8" style={{ alignItems: 'center' }}>
                    {open[prompt.id] ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                  </div>
                </div>

                {open[prompt.id] && (
                  <div className="mt-16">
                    {bewerkt === prompt.id ? (
                      <div>
                        <div className="form-group">
                          <label>Naam</label>
                          <input type="text" className="input" value={formulier.name}
                            onChange={e => setFormulier({ ...formulier, name: e.target.value })} />
                        </div>
                        <div className="form-group">
                          <label>Beschrijving</label>
                          <input type="text" className="input" value={formulier.description || ''}
                            onChange={e => setFormulier({ ...formulier, description: e.target.value })} />
                        </div>
                        <div className="form-group">
                          <label>Prompt Tekst</label>
                          <textarea className="input" rows={12} value={formulier.prompt_text}
                            onChange={e => setFormulier({ ...formulier, prompt_text: e.target.value })}
                            style={{ fontFamily: 'monospace', fontSize: 13 }} spellCheck={false} />
                          {prompt.velden?.length > 0 && (
                            <p className="text-muted text-small mt-4">Moet blijven bevatten: {veldenAlsTekst(prompt.velden)}</p>
                          )}
                        </div>
                        <div className="flex gap-8">
                          <button className="btn btn-success" onClick={() => bewaar(prompt.id)} disabled={bezig}>
                            {bezig ? <RefreshCw size={14} className="spinning" /> : <Save size={14} />} Opslaan
                          </button>
                          <button className="btn btn-secondary" onClick={() => { setBewerkt(null); setFormulier(LEEG); }}>
                            Annuleren
                          </button>
                          {prompt.is_system && (
                            <button className="btn btn-secondary" onClick={() => zetTerug(prompt.id)} disabled={bezig} style={{ marginLeft: 'auto' }}>
                              <RotateCcw size={14} /> Reset naar standaard
                            </button>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div>
                        <pre style={{
                          background: 'var(--wit)',
                          padding: 16,
                          borderRadius: 8,
                          border: '1px solid var(--lijn)',
                          whiteSpace: 'pre-wrap',
                          fontFamily: 'monospace',
                          fontSize: 13,
                          maxHeight: 300,
                          overflow: 'auto',
                        }}>
                          {prompt.prompt_text}
                        </pre>
                        {prompt.velden?.length > 0 && (
                          <p className="text-muted text-small mt-4">Velden die de code invult: {veldenAlsTekst(prompt.velden)}</p>
                        )}
                        <div className="flex gap-8 mt-12">
                          <button className="btn btn-primary" onClick={() => begin(prompt)}>
                            <Edit2 size={14} /> Bewerken
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
        </div>
      )}
    </div>
  );
}
